import { Dispatch } from "redux";
import { getLocale } from "src/i18n/i18n";
import { openSubModal } from "src/store/actions/onlineShopper/subModal";
import { notFound, serverError } from "src/utils/httpStatus";
import { logError } from "src/utils/logger";
import { ErrorResponse } from "Types/errorResponse";
import { getFetchOptionsFromStore } from "../..";

const formatErrorKeysForMessage = (errors: ErrorResponse["errors"] = {}) => {
    return Object.keys(errors)
        .map((key) => key.replace(" ", ""))
        .join(", ");
};

const sendDefaultErrorAction = async (
    error: Response | Error,
    path: string,
    body?: any,
    next?: Dispatch,
    showDefaultErrorMessage: boolean = true
) => {
    if (error instanceof Error) {
        logError(error, { request: { path, body } });
    } else {
        const { status } = error;

        if (!(notFound(status) || serverError(status))) {
            const data = (await error.json()) as ErrorResponse;

            let message: string;

            /* We customize the message for some errors to allow for better grouping in bugsnag */
            switch (data.message) {
                case "Request body contains invalid data!":
                    message = `${path.substring(
                        path.lastIndexOf("/")
                    )} request body contains invalid data: ${formatErrorKeysForMessage(data.errors)}`;
                    break;

                case "Deal contains invalid data!":
                    message = `Deal contains invalid data: ${formatErrorKeysForMessage(data.errors)}`;
                    break;

                default:
                    message = data.message;
                    break;
            }

            logError(new Error(message), { request: { path, body }, response: { data } });
        }
    }

    if (next && showDefaultErrorMessage) {
        next(openSubModal({ modalType: "apologiesModal" }));
    }
};

export const buildHeader = (referer: string) => {
    return {
        "X-Referer": referer,
        "Accept-Language": getLocale(),
        "Content-Type": "application/json; charset=utf-8",
    };
};

export const getHeaderValue = (response: Response, key: string): string | null => {
    if (!response.headers) {
        return null;
    }

    return response.headers.get(key);
};

export const getApiUrl = (path: string) => {
    const { url } = getFetchOptionsFromStore();
    return `${url}${path}`;
};

const baseFetch = async (
    path: string,
    method: string,
    next?: Dispatch,
    body?: any,
    showDefaultErrorMessage?: boolean
): Promise<Response> => {
    const { url, referer } = getFetchOptionsFromStore();
    const headers = buildHeader(referer);

    try {
        const response = await fetch(`${url}${path}`, {
            body,
            headers,
            method,
        });

        if (!response.ok) {
            await sendDefaultErrorAction(response.clone(), path, body, next, showDefaultErrorMessage);
        }

        return response;
    } catch (error) {
        await sendDefaultErrorAction(error, path, body, next, true);
        throw error;
    }
};

export const getFrom = (path: string, next?: Dispatch): Promise<Response> => {
    return baseFetch(path, "GET", next);
};

export const getFromAuthed = async (
    path: string,
    next?: Dispatch,
    showDefaultErrorMessage: boolean = true
): Promise<Response> => {
    return baseFetch(path, "GET", next, undefined, showDefaultErrorMessage);
};

export const postTo = async <T extends object>(
    path: string,
    data: T,
    next?: Dispatch,
    showDefaultErrorMessage?: boolean
): Promise<Response> => {
    return baseFetch(path, "POST", next, JSON.stringify(data), showDefaultErrorMessage);
};

export const putTo = async <T extends object>(
    path: string,
    data: T,
    next?: Dispatch,
    showDefaultErrorMessage?: boolean
): Promise<Response> => {
    return baseFetch(path, "PUT", next, JSON.stringify(data), showDefaultErrorMessage);
};

export const patchTo = async <T extends object>(
    path: string,
    data: T,
    next?: Dispatch,
    showDefaultErrorMessage?: boolean
): Promise<Response> => {
    return baseFetch(path, "PATCH", next, JSON.stringify(data), showDefaultErrorMessage);
};

export const deleteTo = async <T extends object>(
    path: string,
    data: T,
    next?: Dispatch,
    showDefaultErrorMessage?: boolean
): Promise<Response> => {
    return baseFetch(path, "DELETE", next, JSON.stringify(data), showDefaultErrorMessage);
};
